import React, { useState, useEffect } from "react";
import { inventoryApi, categoryApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  FiBox,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiFilter,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiShoppingCart,
  FiShoppingBag,
  FiLayers,
  FiRefreshCw,
} from "react-icons/fi";
import { toast } from "react-toastify";
import Modal from "../components/Modal";

const InventoryPage = () => {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Backend Search & Filters
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Backend Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  // Global KPI Summary Metrics from backend
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalItems: 0,
    totalUnits: 0,
    totalValuation: 0,
    inStockItems: 0,
    inStockUnits: 0,
    inStockValuation: 0,
    soldItems: 0,
    soldUnits: 0,
  });

  // Edit Single Item Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({
    category_id: "",
    subcategory_id: "",
    item_name: "",
    serial_number: "",
    part_number: "",
    sku: "",
    bill_number: "",
    description: "",
    price: "",
    quantity: 1,
    min_stock_alert: 5,
    warranty_start_date: "",
    warranty_end_date: "",
  });

  // Delete Modal State
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
  const [submitting, setSubmitting] = useState(false);

  // Load Categories & Subcategories once
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const [catsRes, subsRes] = await Promise.all([
          categoryApi.getCategories(),
          categoryApi.getSubcategories(),
        ]);
        setCategories(catsRes.data || []);
        setSubcategories(subsRes.data || []);
      } catch (err) {
        console.error("Failed to load categories/subcategories", err);
      }
    };
    loadCategories();
  }, []);

  // Debounce search input by 350ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch Inventory from Backend (with pagination & search)
  const fetchInventory = async (
    page = currentPage,
    limit = pageSize,
    q = debouncedSearch,
    cat = selectedCategoryFilter,
    st = statusFilter
  ) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        search: q.trim() || undefined,
        categoryId: cat || undefined,
        status: st || undefined,
      };
      const res = await inventoryApi.getAll(params);
      setItems(res.data || []);
      if (res.pagination) {
        setPagination(res.pagination);
      } else {
        const total = (res.data || []).length;
        setPagination({
          total,
          page,
          limit,
          totalPages: Math.ceil(total / limit) || 1,
        });
      }
      if (res.summary) {
        setSummaryMetrics({
          totalItems: Number(res.summary.total_items) || 0,
          totalUnits: Number(res.summary.total_units) || 0,
          totalValuation: Number(res.summary.total_valuation) || 0,
          inStockItems: Number(res.summary.available_items) || 0,
          inStockUnits: Number(res.summary.available_units) || 0,
          inStockValuation: Number(res.summary.available_valuation) || 0,
          soldItems: Number(res.summary.sold_items) || 0,
          soldUnits: Number(res.summary.sold_units) || 0,
        });
      }
    } catch (err) {
      toast.error(err.message || "Failed to load inventory data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory(currentPage, pageSize, debouncedSearch, selectedCategoryFilter, statusFilter);
  }, [currentPage, pageSize, debouncedSearch, selectedCategoryFilter, statusFilter]);

  // Open Edit Modal for a single item
  const openEditModal = (item) => {
    if (item.is_sale) {
      toast.warning("Sold items cannot be edited.");
      return;
    }
    setEditingItem(item);
    setEditFormData({
      category_id: String(item.category_id),
      subcategory_id: String(item.subcategory_id),
      item_name: item.item_name || "",
      serial_number: item.serial_number || "",
      part_number: item.part_number || "",
      sku: item.sku || "",
      bill_number: item.bill_number || "",
      description: item.description || "",
      price: item.price !== undefined && item.price !== null ? item.price : "",
      quantity: item.quantity !== undefined && item.quantity !== null ? item.quantity : 1,
      min_stock_alert: item.min_stock_alert !== undefined && item.min_stock_alert !== null ? item.min_stock_alert : 5,
      warranty_start_date: item.warranty_start_date ? item.warranty_start_date.split("T")[0] : "",
      warranty_end_date: item.warranty_end_date ? item.warranty_end_date.split("T")[0] : "",
    });
    setEditModalOpen(true);
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    if (editingItem?.is_sale) {
      toast.error("Sold items cannot be edited!");
      return;
    }
    if (!editFormData.item_name.trim()) {
      toast.warning("Item name is required!");
      return;
    }

    try {
      setSubmitting(true);
      await inventoryApi.update(editingItem.id, editFormData);
      toast.success("Inventory item updated successfully");
      setEditModalOpen(false);
      fetchInventory(currentPage, pageSize, debouncedSearch, selectedCategoryFilter, statusFilter);
    } catch (err) {
      toast.error(err.message || "Failed to update item");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteItem = async () => {
    if (!isAdmin) {
      toast.error("Only administrators are authorized to delete inventory items.");
      setDeleteModal({ open: false, item: null });
      return;
    }
    if (!deleteModal.item) return;
    if (deleteModal.item.is_sale) {
      toast.error("Sold items cannot be deleted!");
      setDeleteModal({ open: false, item: null });
      return;
    }
    try {
      await inventoryApi.delete(deleteModal.item.id);
      toast.success("Item deleted successfully");
      setDeleteModal({ open: false, item: null });
      const newPage = items.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      setCurrentPage(newPage);
      fetchInventory(newPage, pageSize, debouncedSearch, selectedCategoryFilter, statusFilter);
    } catch (err) {
      toast.error(err.message || "Cannot delete item with purchase or sale history");
    }
  };

  return (
    <div className="fade-in">
      {/* Top Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div>
          <h2 style={{ fontSize: "1.5rem", fontWeight: 800, color: "var(--navy)" }}>
            Inventory
          </h2>

        </div>
      </div>

      {/* INVENTORY CATALOG VIEW */}
      <div className="fade-in">
        {/* Inventory Catalog KPI Metrics Summary */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 16,
            marginBottom: 20,
          }}
        >
          {/* Total Items */}
          <div
            onClick={() => {
              setStatusFilter("");
              setCurrentPage(1);
            }}
            style={{
              backgroundColor: !statusFilter ? "#F8FAFC" : "#fff",
              borderRadius: "var(--radius-lg)",
              padding: "16px 20px",
              border: !statusFilter ? "2px solid var(--primary)" : "1px solid var(--slate-200)",
              boxShadow: !statusFilter ? "0 4px 14px rgba(6, 78, 59, 0.12)" : "var(--shadow-sm)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              transition: "all 0.18s ease",
            }}
            title="Click to show all items"
          >
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--slate-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Total Items
                </span>
                {!statusFilter && (
                  <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "1px 6px", borderRadius: 4, backgroundColor: "var(--primary-light)", color: "var(--primary)" }}>
                    Active
                  </span>
                )}
              </div>
              <div style={{ fontSize: "1.55rem", fontWeight: 800, color: "var(--navy)", marginTop: 4 }}>
                {summaryMetrics.totalItems} <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--slate-500)" }}>({summaryMetrics.totalUnits} Units)</span>
              </div>
              <div style={{ fontSize: "0.76rem", fontWeight: 700, color: "var(--primary)", marginTop: 2 }}>
                Rs. {summaryMetrics.totalValuation.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} Total Cost
              </div>
            </div>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                backgroundColor: "var(--primary-light)",
                color: "var(--primary)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FiBox size={22} />
            </div>
          </div>

          {/* Available in Stock */}
          <div
            onClick={() => {
              setStatusFilter(statusFilter === "available" ? "" : "available");
              setCurrentPage(1);
            }}
            style={{
              backgroundColor: statusFilter === "available" ? "#F0FDF4" : "#fff",
              borderRadius: "var(--radius-lg)",
              padding: "16px 20px",
              border: statusFilter === "available" ? "2px solid #15803D" : "1px solid var(--slate-200)",
              boxShadow: statusFilter === "available" ? "0 4px 14px rgba(21, 128, 61, 0.18)" : "var(--shadow-sm)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              transition: "all 0.18s ease",
            }}
            title="Click to filter Available in Stock"
          >
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--slate-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Available in Stock
                </span>
                {statusFilter === "available" && (
                  <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "1px 6px", borderRadius: 4, backgroundColor: "#DCFCE7", color: "#15803D" }}>
                    Active
                  </span>
                )}
              </div>
              <div style={{ fontSize: "1.55rem", fontWeight: 800, color: "#15803D", marginTop: 4 }}>
                {summaryMetrics.inStockItems} <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#15803D" }}>({summaryMetrics.inStockUnits} Units)</span>
              </div>
              <div style={{ fontSize: "0.76rem", fontWeight: 700, color: "#15803D", marginTop: 2 }}>
                Rs. {summaryMetrics.inStockValuation.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} Stock Cost
              </div>
            </div>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                backgroundColor: "#DCFCE7",
                color: "#15803D",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FiLayers size={22} />
            </div>
          </div>

          {/* Sold Items */}
          <div
            onClick={() => {
              setStatusFilter(statusFilter === "sold" ? "" : "sold");
              setCurrentPage(1);
            }}
            style={{
              backgroundColor: statusFilter === "sold" ? "#FAF5FF" : "#fff",
              borderRadius: "var(--radius-lg)",
              padding: "16px 20px",
              border: statusFilter === "sold" ? "2px solid #7E22CE" : "1px solid var(--slate-200)",
              boxShadow: statusFilter === "sold" ? "0 4px 14px rgba(126, 34, 206, 0.18)" : "var(--shadow-sm)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              transition: "all 0.18s ease",
            }}
            title="Click to filter Sold items"
          >
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--slate-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Sold Items
                </span>
                {statusFilter === "sold" && (
                  <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "1px 6px", borderRadius: 4, backgroundColor: "#F3E8FF", color: "#7E22CE" }}>
                    Active
                  </span>
                )}
              </div>
              <div style={{ fontSize: "1.65rem", fontWeight: 800, color: "#7E22CE", marginTop: 4 }}>
                {summaryMetrics.soldItems} <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#7E22CE" }}>({summaryMetrics.soldUnits} Units)</span>
              </div>
            </div>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                backgroundColor: "#F3E8FF",
                color: "#7E22CE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FiShoppingBag size={22} />
            </div>
          </div>
        </div>

        {/* Filter & Search Bar */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            padding: "16px 20px",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
            marginBottom: 20,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <div style={{ position: "relative", flex: 1, minWidth: 260 }}>
            <FiSearch
              size={18}
              style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--slate-400)" }}
            />
            <input
              type="text"
              placeholder="Search by Item Name, Serial Number, Part Number, SKU, Bill, Invoice..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%",
                padding: "9px 36px 9px 38px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--slate-300)",
                fontSize: "0.88rem",
                outline: "none",
              }}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--slate-400)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 2,
                }}
                title="Clear search"
              >
                <FiX size={15} />
              </button>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <FiFilter size={16} color="var(--slate-500)" />
              <select
                value={selectedCategoryFilter}
                onChange={(e) => {
                  setSelectedCategoryFilter(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  padding: "9px 12px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--slate-300)",
                  fontSize: "0.88rem",
                  backgroundColor: "#fff",
                }}
              >
                <option value="">All Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={() => fetchInventory(currentPage, pageSize, debouncedSearch, selectedCategoryFilter, statusFilter)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--slate-200)",
                backgroundColor: "#fff",
                color: "var(--slate-700)",
                cursor: "pointer",
                fontSize: "0.84rem",
                fontWeight: 600,
              }}
              title="Refresh inventory list"
            >
              <FiRefreshCw size={14} className={loading ? "spin" : ""} /> Refresh
            </button>
          </div>
        </div>

        {/* Catalog Table */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
            overflow: "hidden",
          }}
        >
          {/* Table Header with Counts */}
          <div
            style={{
              padding: "16px 20px",
              borderBottom: "1px solid var(--slate-200)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "var(--slate-50)",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  backgroundColor: "var(--primary-light)",
                  color: "var(--primary)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiBox size={16} />
              </div>
              <div>
                <h3 style={{ fontSize: "0.98rem", fontWeight: 700, color: "var(--navy)", margin: 0 }}>
                  Catalog Inventory Items
                </h3>

              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  padding: "4px 12px",
                  borderRadius: 12,
                  backgroundColor: "var(--primary-light)",
                  color: "var(--primary)",
                  border: "1px solid rgba(6, 78, 59, 0.15)",
                }}
              >
                Showing {items.length} of {pagination.total} {pagination.total === 1 ? "Item" : "Items"}
              </span>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
              <thead>
                <tr style={{ backgroundColor: "var(--slate-50)", borderBottom: "1px solid var(--slate-200)", textAlign: "left" }}>
                  <th style={{ padding: "12px 14px", color: "var(--slate-600)", fontWeight: 600, width: 45 }}>#</th>
                  <th style={{ padding: "12px 14px", color: "var(--slate-600)", fontWeight: 600 }}>Item Name</th>
                  <th style={{ padding: "12px 14px", color: "var(--slate-600)", fontWeight: 600 }}>Category & Component</th>
                  <th style={{ padding: "12px 14px", color: "#1D4ED8", fontWeight: 700 }}>Bill #</th>
                  <th style={{ padding: "12px 14px", color: "#9D174D", fontWeight: 700 }}>Invoice #</th>
                  <th style={{ padding: "12px 14px", color: "var(--slate-600)", fontWeight: 600 }}>Part Number</th>
                  <th style={{ padding: "12px 14px", color: "var(--slate-600)", fontWeight: 600 }}>SKU</th>
                  <th style={{ padding: "12px 14px", color: "var(--slate-600)", fontWeight: 600 }}>Serial Number</th>
                  <th style={{ padding: "12px 14px", color: "#065F46", fontWeight: 700, textAlign: "center", width: 75 }}>Unit / Qty</th>
                  <th style={{ padding: "12px 14px", color: "var(--slate-600)", fontWeight: 600, textAlign: "right", minWidth: 110 }}>Unit Cost (Rs.)</th>
                  <th style={{ padding: "12px 14px", color: "var(--navy)", fontWeight: 700, textAlign: "right", minWidth: 120 }}>Total Cost (Rs.)</th>
                  <th style={{ padding: "12px 14px", color: "var(--slate-600)", fontWeight: 600 }}>Warranty Period</th>
                  <th style={{ padding: "12px 14px", color: "var(--slate-600)", fontWeight: 600, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={13} style={{ padding: 40, textAlign: "center", color: "var(--slate-500)" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                        <FiRefreshCw size={18} className="spin" style={{ color: "var(--primary)" }} />
                        <span>Loading inventory from server...</span>
                      </div>
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={13} style={{ padding: 40, textAlign: "center", color: "var(--slate-400)" }}>
                      <div style={{ marginBottom: 10 }}>
                        {debouncedSearch || selectedCategoryFilter || statusFilter
                          ? "No inventory items found matching your filters."
                          : "No inventory items registered in the system."}
                      </div>
                      {(debouncedSearch || selectedCategoryFilter || statusFilter) && (
                        <button
                          onClick={() => {
                            setSearch("");
                            setSelectedCategoryFilter("");
                            setStatusFilter("");
                            setCurrentPage(1);
                          }}
                          style={{
                            padding: "6px 14px",
                            backgroundColor: "var(--slate-100)",
                            color: "var(--slate-700)",
                            border: "1px solid var(--slate-300)",
                            borderRadius: "var(--radius-md)",
                            fontWeight: 600,
                            fontSize: "0.82rem",
                            cursor: "pointer",
                          }}
                        >
                          Clear Filters
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  items.map((item, idx) => {
                    const hasWarranty = item.warranty_start_date || item.warranty_end_date;

                    return (
                      <tr
                        key={item.id}
                        style={{
                          borderBottom: "1px solid var(--slate-100)",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-50)")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        {/* Row Count */}
                        <td style={{ padding: "14px 18px", color: "var(--slate-400)", fontWeight: 600, fontSize: "0.82rem" }}>
                          {(currentPage - 1) * pageSize + idx + 1}
                        </td>
                        {/* Item Name */}
                        <td style={{ padding: "14px 18px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: 8,
                                backgroundColor: "var(--primary-light)",
                                color: "var(--primary)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <FiBox size={17} />
                            </div>
                            <div>
                              <span style={{ fontWeight: 700, color: "var(--navy)" }}>{item.item_name}</span>
                              {item.description && (
                                <div style={{ fontSize: "0.75rem", color: "var(--slate-500)", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {item.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        {/* Category & Related Value */}
                        <td style={{ padding: "14px 18px" }}>
                          <div style={{ fontWeight: 700, color: "var(--slate-800)" }}>{item.category_name}</div>
                        </td>

                        {/* Bill Number */}
                        <td style={{ padding: "14px 14px" }}>
                          {item.bill_number ? (
                            <span
                              style={{
                                fontFamily: "monospace",
                                fontWeight: 700,
                                padding: "3px 8px",
                                borderRadius: 4,
                                backgroundColor: "#EFF6FF",
                                color: "#1D4ED8",
                                border: "1px solid #DBEAFE",
                                fontSize: "0.80rem",
                              }}
                            >
                              {item.bill_number}
                            </span>
                          ) : (
                            <span style={{ color: "var(--slate-400)", fontSize: "0.78rem" }}>—</span>
                          )}
                        </td>

                        {/* Invoice Number */}
                        <td style={{ padding: "14px 14px" }}>
                          {item.invoice_number || item.invoice_no ? (
                            <span
                              style={{
                                fontFamily: "monospace",
                                fontWeight: 700,
                                padding: "3px 8px",
                                borderRadius: 4,
                                backgroundColor: "#FDF2F8",
                                color: "#9D174D",
                                border: "1px solid #FBCFE8",
                                fontSize: "0.80rem",
                              }}
                            >
                              {item.invoice_number || item.invoice_no}
                            </span>
                          ) : (
                            <span style={{ color: "var(--slate-400)", fontSize: "0.78rem" }}>—</span>
                          )}
                        </td>

                        {/* Part Number */}
                        <td style={{ padding: "14px 18px" }}>
                          {item.part_number ? (
                            <span
                              style={{
                                fontFamily: "monospace",
                                color: "var(--slate-700)",
                                fontWeight: 600,
                                fontSize: "0.82rem",
                              }}
                            >
                              {item.part_number}
                            </span>
                          ) : (
                            <span style={{ color: "var(--slate-400)", fontSize: "0.78rem" }}>—</span>
                          )}
                        </td>

                        {/* SKU */}
                        <td style={{ padding: "14px 18px" }}>
                          <span
                            style={{
                              fontFamily: "monospace",
                              fontWeight: 700,
                              padding: "3px 8px",
                              borderRadius: 4,
                              backgroundColor: "var(--slate-100)",
                              color: "var(--primary)",
                              fontSize: "0.82rem",
                            }}
                          >
                            {item.sku}
                          </span>
                        </td>
                        {/* Serial Number */}
                        <td style={{ padding: "14px 18px" }}>
                          {item.serial_number ? (
                            <span
                              style={{
                                fontFamily: "monospace",
                                fontWeight: 700,
                                color: "var(--navy)",
                                backgroundColor: "var(--slate-100)",
                                padding: "2px 6px",
                                borderRadius: 4,
                                fontSize: "0.82rem",
                              }}
                            >
                              {item.serial_number}
                            </span>
                          ) : (
                            <span style={{ color: "var(--slate-400)", fontSize: "0.78rem" }}>—</span>
                          )}
                        </td>

                        {/* Unit / Qty */}
                        <td style={{ padding: "14px 14px", textAlign: "center" }}>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "3px 9px",
                              borderRadius: 6,
                              backgroundColor: (parseInt(item.quantity) || 1) > 1 ? "#ECFDF5" : "#EFF6FF",
                              color: (parseInt(item.quantity) || 1) > 1 ? "#065F46" : "#1D4ED8",
                              fontWeight: 800,
                              fontSize: "0.84rem",
                              border: (parseInt(item.quantity) || 1) > 1 ? "1px solid #A7F3D0" : "1px solid #BFDBFE",
                            }}
                          >
                            {parseInt(item.quantity) || 1}
                          </span>
                        </td>

                        {/* Unit Cost (PKR) */}
                        <td style={{ padding: "14px 14px", textAlign: "right" }}>
                          <span style={{ fontWeight: 700, color: "var(--slate-700)", fontSize: "0.85rem" }}>
                            {item.price !== undefined && item.price !== null && Number(item.price) > 0
                              ? `Rs. ${Number(item.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                              : "—"}
                          </span>
                        </td>

                        {/* Total Cost (PKR) */}
                        <td style={{ padding: "14px 14px", textAlign: "right" }}>
                          <span style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.88rem" }}>
                            {item.price !== undefined && item.price !== null && Number(item.price) > 0
                              ? `Rs. ${(Number(item.price) * (parseInt(item.quantity) || 1)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                              : "—"}
                          </span>
                        </td>

                        {/* Warranty Period */}
                        <td style={{ padding: "14px 18px" }}>
                          {hasWarranty ? (
                            <div>
                              <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--navy)" }}>
                                Ends: {item.warranty_end_date ? new Date(item.warranty_end_date).toLocaleDateString() : "—"}
                              </div>
                              <div style={{ fontSize: "0.72rem", color: "var(--slate-500)" }}>
                                Starts: {item.warranty_start_date ? new Date(item.warranty_start_date).toLocaleDateString() : "—"}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: "var(--slate-400)", fontSize: "0.8rem" }}>No Warranty</span>
                          )}
                        </td>

                        {/* Actions */}
                        <td style={{ padding: "14px 18px", textAlign: "right" }}>
                          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                            {item.is_sale ? (
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                  padding: "5px 12px",
                                  borderRadius: 6,
                                  backgroundColor: "#F3E8FF",
                                  color: "#7E22CE",
                                  fontSize: "0.76rem",
                                  fontWeight: 700,
                                  border: "1px solid rgba(126, 34, 206, 0.2)",
                                }}
                                title="Sold items cannot be edited or deleted"
                              >
                                🔒 Sold (Locked)
                              </span>
                            ) : (
                              <>
                                <button
                                  onClick={() => openEditModal(item)}
                                  style={{
                                    padding: "6px 12px",
                                    borderRadius: 6,
                                    border: "1px solid var(--slate-200)",
                                    backgroundColor: "#fff",
                                    color: "var(--slate-700)",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    fontSize: "0.8rem",
                                    fontWeight: 600,
                                    transition: "all 0.15s ease",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "var(--primary-light)";
                                    e.currentTarget.style.color = "var(--primary)";
                                    e.currentTarget.style.borderColor = "var(--primary)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "#fff";
                                    e.currentTarget.style.color = "var(--slate-700)";
                                    e.currentTarget.style.borderColor = "var(--slate-200)";
                                  }}
                                >
                                  <FiEdit2 size={14} /> Edit
                                </button>
                                {isAdmin && (
                                  <button
                                    onClick={() => setDeleteModal({ open: true, item })}
                                    style={{
                                      padding: "6px 12px",
                                      borderRadius: 6,
                                      border: "1px solid #FECACA",
                                      backgroundColor: "#FEF2F2",
                                      color: "var(--danger)",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 4,
                                      fontSize: "0.8rem",
                                      fontWeight: 600,
                                      transition: "all 0.15s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = "var(--danger)";
                                      e.currentTarget.style.color = "#fff";
                                      e.currentTarget.style.borderColor = "var(--danger)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = "#FEF2F2";
                                      e.currentTarget.style.color = "var(--danger)";
                                      e.currentTarget.style.borderColor = "#FECACA";
                                    }}
                                  >
                                    <FiTrash2 size={14} /> Delete
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {pagination.total > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
                padding: "14px 20px",
                borderTop: "1px solid var(--slate-200)",
                backgroundColor: "var(--slate-50)",
                fontSize: "0.84rem",
                color: "var(--slate-600)",
              }}
            >
              <div>
                Showing <strong>{Math.min((currentPage - 1) * pageSize + 1, pagination.total)}</strong> to{" "}
                <strong>{Math.min(currentPage * pageSize, pagination.total)}</strong> of{" "}
                <strong>{pagination.total}</strong> items
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                {/* Rows Per Page Selector */}
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: "0.82rem", fontWeight: 600 }}>Rows:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      const newLimit = Number(e.target.value);
                      setPageSize(newLimit);
                      setCurrentPage(1);
                    }}
                    style={{
                      padding: "4px 8px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-300)",
                      fontSize: "0.82rem",
                      backgroundColor: "#fff",
                      fontWeight: 600,
                      color: "var(--navy)",
                      cursor: "pointer",
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>

                {/* Prev Button */}
                <button
                  type="button"
                  disabled={currentPage === 1 || loading}
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "5px 12px",
                    borderRadius: 6,
                    border: "1px solid var(--slate-300)",
                    backgroundColor: currentPage === 1 ? "var(--slate-100)" : "#fff",
                    color: currentPage === 1 ? "var(--slate-400)" : "var(--slate-700)",
                    cursor: currentPage === 1 || loading ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    transition: "all 0.15s ease",
                  }}
                >
                  <FiChevronLeft size={15} /> Prev
                </button>

                {/* Numbered Page Buttons */}
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                    .filter((p) => {
                      if (pagination.totalPages <= 7) return true;
                      if (p === 1 || p === pagination.totalPages) return true;
                      if (Math.abs(p - currentPage) <= 1) return true;
                      return false;
                    })
                    .map((p, idx, arr) => {
                      const prev = arr[idx - 1];
                      const showEllipsis = prev && p - prev > 1;

                      return (
                        <React.Fragment key={p}>
                          {showEllipsis && (
                            <span style={{ padding: "0 4px", color: "var(--slate-400)" }}>...</span>
                          )}
                          <button
                            type="button"
                            disabled={loading}
                            onClick={() => setCurrentPage(p)}
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: 6,
                              border: p === currentPage ? "1.5px solid var(--primary)" : "1px solid var(--slate-300)",
                              backgroundColor: p === currentPage ? "var(--primary)" : "#fff",
                              color: p === currentPage ? "#fff" : "var(--slate-700)",
                              fontWeight: 700,
                              fontSize: "0.8rem",
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                            }}
                          >
                            {p}
                          </button>
                        </React.Fragment>
                      );
                    })}
                </div>

                {/* Next Button */}
                <button
                  type="button"
                  disabled={currentPage >= pagination.totalPages || loading}
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, pagination.totalPages))}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "5px 12px",
                    borderRadius: 6,
                    border: "1px solid var(--slate-300)",
                    backgroundColor: currentPage >= pagination.totalPages ? "var(--slate-100)" : "#fff",
                    color: currentPage >= pagination.totalPages ? "var(--slate-400)" : "var(--slate-700)",
                    cursor: currentPage >= pagination.totalPages || loading ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    transition: "all 0.15s ease",
                  }}
                >
                  Next <FiChevronRight size={15} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* EDIT SINGLE ITEM MODAL                                                    */}
      {/* ========================================================================= */}
      <Modal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth={540}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ fontSize: "1.2rem", fontWeight: 800, color: "var(--navy)" }}>
            Edit Inventory Item
          </h3>
          <button onClick={() => setEditModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}>
            <FiX size={20} color="var(--slate-400)" />
          </button>
        </div>

        <form onSubmit={handleUpdateItem}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
              Item Name *
            </label>
            <input
              type="text"
              required
              value={editFormData.item_name}
              onChange={(e) => setEditFormData({ ...editFormData, item_name: e.target.value })}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--slate-300)" }}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                Serial Number
              </label>
              <input
                type="text"
                placeholder="Serial number..."
                value={editFormData.serial_number}
                onChange={(e) => setEditFormData({ ...editFormData, serial_number: e.target.value })}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--slate-300)", fontFamily: "monospace" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                Part Number
              </label>
              <input
                type="text"
                placeholder="Part number..."
                value={editFormData.part_number}
                onChange={(e) => setEditFormData({ ...editFormData, part_number: e.target.value })}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--slate-300)", fontFamily: "monospace" }}
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                SKU Number
              </label>
              <input
                type="text"
                value={editFormData.sku}
                onChange={(e) => setEditFormData({ ...editFormData, sku: e.target.value })}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--slate-300)", fontFamily: "monospace" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                Bill / Invoice #
              </label>
              <input
                type="text"
                placeholder="e.g. BILL-2026-001"
                value={editFormData.bill_number}
                onChange={(e) => setEditFormData({ ...editFormData, bill_number: e.target.value })}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--slate-300)", fontFamily: "monospace" }}
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr 1.2fr", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                Units / Qty
              </label>
              <input
                type="number"
                min="1"
                step="1"
                placeholder="1"
                value={editFormData.quantity}
                onChange={(e) => setEditFormData({ ...editFormData, quantity: e.target.value })}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--slate-300)", fontWeight: 700 }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "#92400E", marginBottom: 6 }}>
                Bottom Line (Alert Limit) *
              </label>
              <input
                type="number"
                min="0"
                step="1"
                placeholder="5"
                value={editFormData.min_stock_alert}
                onChange={(e) => setEditFormData({ ...editFormData, min_stock_alert: e.target.value })}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1.5px solid #FCD34D",
                  backgroundColor: "#FFFBEB",
                  color: "#92400E",
                  fontWeight: 800,
                }}
              />
              <span style={{ fontSize: "0.70rem", color: "#B45309", marginTop: 3, display: "block" }}>
                Alerts when count reaches this
              </span>
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                Unit Cost (Rs.)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={editFormData.price}
                onChange={(e) => setEditFormData({ ...editFormData, price: e.target.value })}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--slate-300)" }}
              />
            </div>
          </div>

          {editFormData.price && Number(editFormData.price) > 0 && (
            <div
              style={{
                marginBottom: 14,
                fontSize: "0.82rem",
                color: "var(--navy)",
                fontWeight: 700,
                backgroundColor: "#F8FAFC",
                padding: "8px 14px",
                borderRadius: 8,
                border: "1px solid var(--slate-200)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Total Cost Calculation:</span>
              <span style={{ color: "var(--primary)", fontWeight: 800 }}>
                {parseInt(editFormData.quantity) || 1} Units × Rs. {Number(editFormData.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} = Rs. {((parseInt(editFormData.quantity) || 1) * Number(editFormData.price)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                Warranty Start Date
              </label>
              <input
                type="date"
                value={editFormData.warranty_start_date}
                onChange={(e) => setEditFormData({ ...editFormData, warranty_start_date: e.target.value })}
                style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid var(--slate-300)", backgroundColor: "#fff" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                Warranty End Date
              </label>
              <input
                type="date"
                value={editFormData.warranty_end_date}
                onChange={(e) => setEditFormData({ ...editFormData, warranty_end_date: e.target.value })}
                style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid var(--slate-300)", backgroundColor: "#fff" }}
              />
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20, flexWrap: "wrap", gap: 12 }}>
            {isAdmin ? (
              <button
                type="button"
                onClick={() => {
                  const itemToDelete = editingItem;
                  setEditModalOpen(false);
                  setDeleteModal({ open: true, item: itemToDelete });
                }}
                style={{
                  padding: "9px 16px",
                  borderRadius: 8,
                  border: "1px solid #FECACA",
                  backgroundColor: "#FEF2F2",
                  color: "var(--danger)",
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  transition: "all 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--danger)";
                  e.currentTarget.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#FEF2F2";
                  e.currentTarget.style.color = "var(--danger)";
                }}
              >
                <FiTrash2 size={15} /> Delete Item
              </button>
            ) : <div />}

            <div style={{ display: "flex", gap: 12 }}>
              <button
                type="button"
                onClick={() => setEditModalOpen(false)}
                style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid var(--slate-300)", background: "#fff", cursor: "pointer", fontWeight: 600 }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                style={{ padding: "9px 22px", borderRadius: 8, border: "none", background: "var(--primary)", color: "#fff", fontWeight: 700, cursor: "pointer" }}
              >
                {submitting ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* ========================================================================= */}
      {/* DELETE CONFIRMATION MODAL                                                 */}
      {/* ========================================================================= */}
      <Modal isOpen={deleteModal.open} onClose={() => setDeleteModal({ open: false, item: null })} maxWidth={400}>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              backgroundColor: "var(--danger-bg)",
              color: "var(--danger)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <FiTrash2 size={24} />
          </div>
          <h4 style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--navy)", marginBottom: 8 }}>
            Delete Inventory Item
          </h4>
          <p style={{ fontSize: "0.85rem", color: "var(--slate-500)", marginBottom: 20 }}>
            Are you sure you want to delete <strong>"{deleteModal.item?.item_name}"</strong>?
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
            <button
              type="button"
              onClick={() => setDeleteModal({ open: false, item: null })}
              style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid var(--slate-300)", background: "#fff", cursor: "pointer", fontWeight: 600 }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteItem}
              style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "var(--danger)", color: "#fff", fontWeight: 700, cursor: "pointer" }}
            >
              Delete Item
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InventoryPage;
